/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Current request status.
 */
export const RiskPolicyBypassRequestStatus = {
  Requested: "requested",
  Approved: "approved",
  Denied: "denied",
  Revoked: "revoked",
} as const;
/**
 * Current request status.
 */
export type RiskPolicyBypassRequestStatus = OpenEnum<
  typeof RiskPolicyBypassRequestStatus
>;

export type RiskPolicyBypassRequest = {
  /**
   * Creation timestamp.
   */
  createdAt: Date;
  /**
   * Decision timestamp.
   */
  decidedAt?: Date | undefined;
  /**
   * User ID that approved, denied, or revoked the request.
   */
  decidedBy?: string | undefined;
  /**
   * Principal URNs granted when approved.
   */
  grantedPrincipalUrns: Array<string>;
  /**
   * The bypass request ID.
   */
  id: string;
  /**
   * Requester note.
   */
  note?: string | undefined;
  /**
   * The risk policy ID.
   */
  policyId: string;
  /**
   * Requester email when known.
   */
  requesterEmail?: string | undefined;
  /**
   * Requester user ID.
   */
  requesterUserId: string;
  /**
   * Current request status.
   */
  status: RiskPolicyBypassRequestStatus;
  /**
   * Selector dimensions for the request target.
   */
  targetDimensions: { [k: string]: string };
  /**
   * Canonical key for the target.
   */
  targetKey?: string | undefined;
  /**
   * Optional target namespace for the request, such as server_url.
   */
  targetKind?: string | undefined;
  /**
   * Optional display label for the target.
   */
  targetLabel?: string | undefined;
  /**
   * Last update timestamp.
   */
  updatedAt: Date;
};

/** @internal */
export const RiskPolicyBypassRequestStatus$inboundSchema: z.ZodMiniType<
  RiskPolicyBypassRequestStatus,
  unknown
> = openEnums.inboundSchema(RiskPolicyBypassRequestStatus);

/** @internal */
export const RiskPolicyBypassRequest$inboundSchema: z.ZodMiniType<
  RiskPolicyBypassRequest,
  unknown
> = z.pipe(
  z.object({
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    decided_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    decided_by: z.optional(z.string()),
    granted_principal_urns: z.array(z.string()),
    id: z.string(),
    note: z.optional(z.string()),
    policy_id: z.string(),
    requester_email: z.optional(z.string()),
    requester_user_id: z.string(),
    status: RiskPolicyBypassRequestStatus$inboundSchema,
    target_dimensions: z.record(z.string(), z.string()),
    target_key: z.optional(z.string()),
    target_kind: z.optional(z.string()),
    target_label: z.optional(z.string()),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "created_at": "createdAt",
      "decided_at": "decidedAt",
      "decided_by": "decidedBy",
      "granted_principal_urns": "grantedPrincipalUrns",
      "policy_id": "policyId",
      "requester_email": "requesterEmail",
      "requester_user_id": "requesterUserId",
      "target_dimensions": "targetDimensions",
      "target_key": "targetKey",
      "target_kind": "targetKind",
      "target_label": "targetLabel",
      "updated_at": "updatedAt",
    });
  }),
);

export function riskPolicyBypassRequestFromJSON(
  jsonString: string,
): SafeParseResult<RiskPolicyBypassRequest, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RiskPolicyBypassRequest$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RiskPolicyBypassRequest' from JSON`,
  );
}
